import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { searchFilms, hotFilms, categories } from "@/lib/films";

const HISTORY_KEY = "sanjipian_search_history";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(HISTORY_KEY);
      if (saved) {
        const parsed: unknown = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setHistory(parsed.filter((item): item is string => typeof item === "string").slice(0, 6));
        }
      }
    } catch {
      setHistory([]);
    }
  }, []);

  useDocumentMeta(
    query ? `搜索“${query}”的结果 - 三级片影视大全` : "影片搜索 - 三级片影视大全",
    query
      ? `在三级片影视大全中搜索“${query}”，按片名、导演、主演与剧情关键词匹配影片资料，查看剧情介绍与同类推荐。`
      : "输入片名、导演或剧情关键词，快速检索三级片影视大全中的影片资料与分类片单。",
    query ? `${query},三级片搜索,三级片资源` : "三级片搜索,三级片查找,三级片资源,三级片大全"
  );

  const results = useMemo(() => searchFilms(query), [query]);
  const recommended = useMemo(() => hotFilms(4), []);

  const commit = (value: string) => {
    const next = value.trim();
    if (!next) {
      setSearchParams({}, { replace: true });
      return;
    }
    setSearchParams({ q: next }, { replace: false });
    try {
      const updated = [next, ...history.filter((item) => item !== next)].slice(0, 6);
      setHistory(updated);
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    } catch {
      // 本地记录不可用时忽略即可
    }
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    commit(keyword);
  };

  const clearHistory = () => {
    setHistory([]);
    try {
      window.localStorage.removeItem(HISTORY_KEY);
    } catch {
      // ignore
    }
  };

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-300 bg-background-100 pt-16 md:pt-[72px]">
        <div className="mx-auto w-full max-w-[1440px] px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-12 lg:px-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-500">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">搜索</span>
          </nav>

          <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {query ? `“${query}”的搜索结果` : "搜索影片资料"}
          </h1>
          <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-foreground-600">
            支持按片名、导演、主演与剧情关键词模糊匹配。输入关键词后回车即可查询，搜索记录仅保存在本机浏览器。
          </p>

          <form onSubmit={onSubmit} className="mt-6 w-full max-w-3xl">
            <div className="flex h-12 w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 pl-4 pr-1.5 transition-colors focus-within:border-accent-500">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                type="text"
                name="keyword"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：夜航码头、雨夜、陈墨白"
                aria-label="搜索关键词"
                className="h-full min-w-0 flex-1 bg-transparent font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-foreground-500 transition-colors hover:text-foreground-900"
                  aria-label="清空输入"
                >
                  <i className="ri-close-line" />
                </button>
              )}
              <button
                type="submit"
                className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                搜索
              </button>
            </div>
          </form>

          {history.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="font-label text-xs text-foreground-500">最近搜索</span>
              {history.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => commit(item)}
                  className="rounded-full border border-background-300 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500"
                >
                  {item}
                </button>
              ))}
              <button
                type="button"
                onClick={clearHistory}
                className="font-label text-xs text-foreground-500 transition-colors hover:text-accent-500"
              >
                清除
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14 lg:px-12">
        {query && results.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-accent-500 px-3 py-1 font-label text-xs text-background-50">
              找到 {results.length} 部影片
            </span>
            <span className="font-label text-xs text-foreground-500">关键词：{query}</span>
          </div>
        )}

        {results.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
            {results.map((film, index) => (
              <FilmCard key={film.id} film={film} index={index} showTagline />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-file-search-line text-2xl" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                {query ? "没有找到匹配的影片" : "输入关键词开始搜索"}
              </h2>
              <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-foreground-600">
                {query
                  ? "试试更短的关键词，或者用导演名、年份、类型来代替完整片名。也可以从下面的分类片单开始浏览。"
                  : "可以搜索片名（如「夜航码头」）、导演（如「陈墨白」）或剧情关键词（如「雨夜」）。"}
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                {categories.slice(0, 4).map((category) => (
                  <Link
                    key={category.key}
                    to={`/category?type=${category.key}`}
                    className="rounded-full border border-background-300 px-3.5 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500"
                  >
                    {category.name}
                  </Link>
                ))}
                <Link
                  to="/category"
                  className="rounded-full bg-accent-500 px-3.5 py-1.5 font-label text-xs text-background-50 transition-colors hover:bg-accent-600"
                >
                  浏览全部分类
                </Link>
              </div>
            </div>

            <aside className="rounded-lg border border-background-300 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">大家都在看</h2>
              <div className="mt-4 flex flex-col gap-3">
                {recommended.map((film) => (
                  <Link key={film.id} to={`/detail/${film.id}`} className="group flex items-center gap-3">
                    <span className="relative h-16 w-11 shrink-0 overflow-hidden rounded border border-background-300">
                      <img
                        src={film.poster}
                        alt={`《${film.title}》缩略海报`}
                        title={`${film.title} 海报`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-label text-sm text-foreground-900 transition-colors group-hover:text-accent-500">
                        {film.title}
                      </span>
                      <span className="mt-1 block font-label text-[11px] text-foreground-500">
                        {film.year} · {film.genres.join(" / ")}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </aside>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}